import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import Layout from '../../components/Layout'
import { PersonSummary, DeletionManifest as DeletionManifestType } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  user: PersonSummary
  manifest: DeletionManifestType
  transfer_candidates: { id: number; name: string }[]
}

export default function DeletionManifest() {
  const { user, manifest, transfer_candidates } = usePage().props as unknown as Props
  const [disposition, setDisposition] = useState<'reassign' | 'orphan'>('reassign')
  const [transferTo, setTransferTo] = useState('')
  const [confirmWord, setConfirmWord] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isMultiTenant = manifest.is_multi_tenant
  const otherTenants = (manifest.tenants || []).filter(t => !t.is_current)

  const totalItems = manifest.rocks.length + manifest.action_items_open.length +
    manifest.metrics.length + manifest.topics_open.length

  const actionTitle = isMultiTenant
    ? `Remove ${user.full_name} from this account`
    : `Delete ${user.full_name}`

  const canSubmit = confirmWord === 'DELETE' && (disposition === 'orphan' || !!transferTo)

  const handleDelete = () => {
    if (!canSubmit) return
    setSubmitting(true)
    router.delete(`/people/${user.id}`, {
      data: {
        disposition,
        transfer_to_user_id: disposition === 'reassign' ? transferTo : '',
        confirmation: confirmWord,
      },
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Layout>
      <Head title={`Delete ${user.full_name}`} />
      <div className="max-w-3xl mx-auto px-6 py-6">
        <Link href={`/people/${user.id}`} className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors mb-4">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {user.full_name}
        </Link>

        {isMultiTenant && (
          <div className="bg-charcoal/10 border border-charcoal/30 rounded-xl p-4 mb-4">
            <h2 className="text-sm font-semibold text-text-primary mb-1">This person belongs to more than one account</h2>
            <p className="text-sm text-text-secondary mb-3">
              You are only removing them from <span className="font-medium">this</span> account. Their login and their
              membership in the following accounts will be left completely untouched:
            </p>
            <ul className="space-y-1">
              {otherTenants.map(t => (
                <li key={t.organization_id} className="text-sm text-text-primary flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-charcoal" />
                  <span className="font-medium">{t.organization_name || 'Unknown account'}</span>
                  <span className="text-text-secondary">— {t.role}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-white rounded-xl border border-pinnacle-red/20 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-pinnacle-red/5 border-b border-pinnacle-red/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-pinnacle-red/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-pinnacle-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-bold text-text-primary font-heading">{actionTitle}</h1>
                <p className="text-sm text-text-secondary">
                  {isMultiTenant
                    ? 'This removes them from this account only and cannot be undone.'
                    : 'This action cannot be undone.'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {totalItems > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-text-primary mb-3">Items to Transfer</h2>
                <div className="space-y-2">
                  {manifest.rocks.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-stone/10 rounded-lg">
                      <span className="text-sm text-text-primary">Active Rocks</span>
                      <span className="text-sm font-medium text-text-primary">{manifest.rocks.length}</span>
                    </div>
                  )}
                  {manifest.action_items_open.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-stone/10 rounded-lg">
                      <span className="text-sm text-text-primary">Open Action Items</span>
                      <span className="text-sm font-medium text-text-primary">{manifest.action_items_open.length}</span>
                    </div>
                  )}
                  {manifest.metrics.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-stone/10 rounded-lg">
                      <span className="text-sm text-text-primary">Active Metrics</span>
                      <span className="text-sm font-medium text-text-primary">{manifest.metrics.length}</span>
                    </div>
                  )}
                  {manifest.topics_open.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-stone/10 rounded-lg">
                      <span className="text-sm text-text-primary">Open Topics</span>
                      <span className="text-sm font-medium text-text-primary">{manifest.topics_open.length}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {manifest.direct_reports.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-text-primary mb-2">Direct Reports to Reassign</h2>
                <div className="flex flex-wrap gap-2">
                  {manifest.direct_reports.map(r => (
                    <span key={r.id} className="text-sm bg-stone/20 text-text-primary px-2 py-1 rounded-full">{r.name}</span>
                  ))}
                </div>
              </div>
            )}

            {manifest.team_memberships.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-text-primary mb-2">Team Memberships to Remove</h2>
                <div className="flex flex-wrap gap-2">
                  {/* allow-unsorted-people: this lists the teams one person
                      belongs to, not a list of people. */}
                  {manifest.team_memberships.map((tm, i) => (
                    <span key={i} className="text-sm bg-stone/20 text-text-primary px-2 py-1 rounded-full">{tm.team_name} ({tm.role})</span>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-stone/20 pt-5 space-y-3">
              <h2 className="text-sm font-semibold text-text-primary">What should happen to their items?</h2>

              <label className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-stone/30 cursor-pointer hover:bg-stone/5">
                <input
                  type="radio"
                  name="disposition"
                  className="mt-0.5"
                  checked={disposition === 'reassign'}
                  onChange={() => setDisposition('reassign')}
                />
                <span className="text-sm text-text-primary">
                  <span className="font-medium">Reassign to another person</span>
                  <span className="block text-text-secondary">Rocks, action items, metrics, topics and direct reports move to the person you choose.</span>
                </span>
              </label>

              <label className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-stone/30 cursor-pointer hover:bg-stone/5">
                <input
                  type="radio"
                  name="disposition"
                  className="mt-0.5"
                  checked={disposition === 'orphan'}
                  onChange={() => setDisposition('orphan')}
                />
                <span className="text-sm text-text-primary">
                  <span className="font-medium">Leave items unassigned</span>
                  <span className="block text-text-secondary">Items stay in this account without an owner. Team memberships are removed and direct reports lose their manager.</span>
                </span>
              </label>

              {disposition === 'reassign' && (
                <div className="pt-1">
                  <label className="block text-sm font-semibold text-text-primary mb-1.5">Reassign all items to</label>
                  <select
                    value={transferTo}
                    onChange={e => setTransferTo(e.target.value)}
                    className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                  >
                    <option value="">Select a person...</option>
                    {sortPeople(transfer_candidates).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-text-primary mb-1.5">
                Type <span className="font-mono text-pinnacle-red">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={confirmWord}
                onChange={e => setConfirmWord(e.target.value)}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/40 focus:border-pinnacle-red font-mono"
                placeholder="DELETE"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Link
                href={`/people/${user.id}`}
                className="px-4 py-2 text-sm font-medium text-text-primary hover:text-text-primary transition-colors"
              >
                Cancel
              </Link>
              <button
                onClick={handleDelete}
                disabled={!canSubmit || submitting}
                className="px-5 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? (isMultiTenant ? 'Removing...' : 'Deleting...')
                  : (isMultiTenant ? 'Remove From This Account' : 'Permanently Delete User')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
